import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { MOBILE_PATH_PREFIX } from '@root/query/constants'
import { MobileEmbedMessages } from '@root/constants'

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage?: (message: string) => void
    }
  }
}

interface MobileEmbedContextValue {
  isEmbedded: boolean
  isRedirecting: boolean
  postMessage: (msg: string) => void
  redirectURL?: string
  errorRedirectURL?: string
}

const DEFAULT_CONTEXT: MobileEmbedContextValue = {
  isEmbedded: false,
  isRedirecting: false,
  postMessage: () => undefined,
  redirectURL: undefined,
  errorRedirectURL: undefined
}

const getHasReactNativeBridge = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  return Boolean((window as any)?.ReactNativeWebView?.postMessage)
}

const MobileEmbedContext = React.createContext<MobileEmbedContextValue>(DEFAULT_CONTEXT)

const REDIRECT_QUERY_KEY = 'redirect-url'
const ERROR_REDIRECT_QUERY_KEY = 'error-redirect-url'
const EMBEDDED_REDIRECT_PATTERN = /^(nocfo|nocfo-prev|nocfo-dev):\/\//i

const sanitizeEmbeddedRedirect = (value?: string | null): string | undefined => {
  if (!value) return undefined

  const normalized = value.trim()
  if (!normalized) return undefined

  // Making sure only the nocfo:// scheme is allowed for embedded redirects
  return EMBEDDED_REDIRECT_PATTERN.test(normalized) ? normalized : undefined
}

const extractRedirectURL = (search: string, key: string): string | undefined => {
  if (!search) return undefined

  const params = new URLSearchParams(search)
  const value = params.get(key)
  return sanitizeEmbeddedRedirect(value)
}

export const MobileEmbedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const history = useHistory()

  const [hasBridge, setHasBridge] = useState<boolean>(() => getHasReactNativeBridge())
  const { pathname, search, hash } = location
  const isMobilePath = pathname.startsWith(MOBILE_PATH_PREFIX)
  const [redirectURL] = useState<string | undefined>(() =>
    extractRedirectURL(search, REDIRECT_QUERY_KEY)
  )
  const [errorRedirectURL] = useState<string | undefined>(() =>
    extractRedirectURL(search, ERROR_REDIRECT_QUERY_KEY)
  )

  useEffect(() => {
    if (hasBridge || isMobilePath) return
    if (typeof window === 'undefined') return

    // Redundancy check if for some reason
    // the bridge has not been detected on initial load.
    const intervalId = window.setTimeout(() => {
      if (getHasReactNativeBridge()) {
        setHasBridge(true)
        window.clearTimeout(intervalId)
      }
    }, 500)

    return () => window.clearTimeout(intervalId)
  }, [hasBridge, isMobilePath])

  const isEmbedded = hasBridge || isMobilePath
  const isRedirecting = isEmbedded && !isMobilePath

  useEffect(() => {
    // Makes sure redirects from BE or
    // within the app are redirected to /mobile paths.

    if (!isEmbedded) return
    if (isMobilePath) return
    // Users that are embedded but
    // are redirected to non-mobile path.

    history.replace(`${MOBILE_PATH_PREFIX}${pathname}${search}${hash}`)
  }, [hash, history, isEmbedded, isMobilePath, pathname, search])

  const postMessage = useCallback((msg: MobileEmbedMessages) => {
    window.ReactNativeWebView?.postMessage?.(JSON.stringify({ message: msg }))
  }, [])

  const contextValue = useMemo<MobileEmbedContextValue>(
    () => ({
      isEmbedded,
      isRedirecting,
      postMessage,
      redirectURL,
      errorRedirectURL
    }),
    [isEmbedded, isRedirecting, postMessage, redirectURL, errorRedirectURL]
  )

  return <MobileEmbedContext.Provider value={contextValue}>{children}</MobileEmbedContext.Provider>
}

export const useMobileEmbed = (): MobileEmbedContextValue => {
  return useContext(MobileEmbedContext)
}
